/**
 * Wire Crossing Detection
 *
 * FOR: pricing-portal-laravel/resources/js/components/sites/documentation-tabs/sax/crossingDetector.js
 *
 * Detects intersections between orthogonal wire paths and determines z-order for bridge rendering
 */

/**
 * Detect all crossings between wire paths
 *
 * @param {Array} waypoints - Array of {x, y} waypoint objects for all links
 * @returns {Array} Array of crossing objects
 */
export function detectCrossings(linkWaypoints) {
  const crossings = [];

  // Get all link IDs
  const linkIds = Object.keys(linkWaypoints);

  // Check each pair of links for intersections
  for (let i = 0; i < linkIds.length; i++) {
    for (let j = i + 1; j < linkIds.length; j++) {
      const link1Id = linkIds[i];
      const link2Id = linkIds[j];

      const waypoints1 = linkWaypoints[link1Id];
      const waypoints2 = linkWaypoints[link2Id];

      if (!waypoints1 || !waypoints2) {continue;}

      // Check each segment of link1 against each segment of link2
      for (let seg1 = 0; seg1 < waypoints1.length - 1; seg1++) {
        const p1 = waypoints1[seg1];
        const p2 = waypoints1[seg1 + 1];

        for (let seg2 = 0; seg2 < waypoints2.length - 1; seg2++) {
          const p3 = waypoints2[seg2];
          const p4 = waypoints2[seg2 + 1];

          const intersection = getLineIntersection(p1, p2, p3, p4);

          if (intersection) {
            crossings.push({
              point: intersection,
              link1: link1Id,
              link2: link2Id,
              segment1Index: seg1,
              segment2Index: seg2,
              // Determine which line is horizontal/vertical for bridge rendering
              isLink1Horizontal: Math.abs(p2.y - p1.y) < Math.abs(p2.x - p1.x),
              isLink2Horizontal: Math.abs(p4.y - p3.y) < Math.abs(p4.x - p3.x),
            });
          }
        }
      }
    }
  }

  return crossings;
}

/**
 * Get intersection point between two line segments
 * Uses parametric line equation to find intersection
 *
 * @param {Object} p1 - Start of line 1
 * @param {Object} p2 - End of line 1
 * @param {Object} p3 - Start of line 2
 * @param {Object} p4 - End of line 2
 * @returns {Object|null} Intersection point {x, y} or null if no intersection
 */
function getLineIntersection(p1, p2, p3, p4) {
  const x1 = p1.x;
  const y1 = p1.y;
  const x2 = p2.x;
  const y2 = p2.y;
  const x3 = p3.x;
  const y3 = p3.y;
  const x4 = p4.x;
  const y4 = p4.y;

  // Calculate direction vectors
  const dx1 = x2 - x1;
  const dy1 = y2 - y1;
  const dx2 = x4 - x3;
  const dy2 = y4 - y3;

  // Calculate determinant (cross product)
  const denominator = dx1 * dy2 - dy1 * dx2;

  // Lines are parallel or coincident
  if (Math.abs(denominator) < 0.0001) {
    return null;
  }

  // Calculate parametric values
  const t = ((x3 - x1) * dy2 - (y3 - y1) * dx2) / denominator;
  const u = ((x3 - x1) * dy1 - (y3 - y1) * dx1) / denominator;

  // Check if intersection is within both line segments
  // Use small tolerance to catch endpoint overlaps
  const tolerance = 0.01;
  if (t >= -tolerance && t <= 1 + tolerance && u >= -tolerance && u <= 1 + tolerance) {
    // Calculate intersection point
    const x = x1 + t * dx1;
    const y = y1 + t * dy1;

    // Ignore endpoint crossings (where wires just touch at corners)
    // Only report true crossings where wires pass through each other
    const isNearStart1 = t < 0.1;
    const isNearEnd1 = t > 0.9;
    const isNearStart2 = u < 0.1;
    const isNearEnd2 = u > 0.9;

    if ((isNearStart1 || isNearEnd1) && (isNearStart2 || isNearEnd2)) {
      return null; // Corner touch, not a crossing
    }

    return { x, y };
  }

  return null;
}

/**
 * Assign z-order to links for crossing rendering
 * Links created earlier go under, newer links go over
 *
 * @param {Object} linkWaypoints - Map of linkId -> waypoints
 * @param {Array} crossings - Array of crossing objects
 * @returns {Map} Map of linkId -> z-order priority (higher = on top)
 */
export function assignZOrder(linkWaypoints, crossings) {
  const zOrder = new Map();

  // Assign z-order based on link creation order (object key order)
  const linkIds = Object.keys(linkWaypoints);
  linkIds.forEach((linkId, index) => {
    zOrder.set(linkId, index);
  });

  return zOrder;
}

/**
 * Get crossings for a specific link
 *
 * @param {String} linkId - The link ID to get crossings for
 * @param {Array} crossings - All crossings
 * @returns {Array} Crossings that involve this link
 */
export function getCrossingsForLink(linkId, crossings) {
  return crossings.filter((crossing) => crossing.link1 === linkId || crossing.link2 === linkId);
}

/**
 * Determine if this link should show a bridge at a crossing point
 *
 * @param {String} linkId - The link being rendered
 * @param {Object} crossing - The crossing object
 * @param {Map} zOrder - Z-order assignments
 * @returns {Boolean} True if this link should show a bridge (goes under)
 */
export function shouldShowBridge(linkId, crossing, zOrder) {
  const link1Order = zOrder.get(crossing.link1) || 0;
  const link2Order = zOrder.get(crossing.link2) || 0;

  // The link with lower z-order (created earlier) shows the bridge
  if (crossing.link1 === linkId) {
    return link1Order < link2Order;
  } else {
    return link2Order < link1Order;
  }
}

/**
 * Calculate bridge arc points for crossing rendering
 * Creates a small arc that jumps over the crossing wire
 *
 * @param {Object} crossing - The crossing object
 * @param {Array} waypoints - Waypoints for the link showing the bridge
 * @param {Number} bridgeSize - Size of the bridge arc (default 8px)
 * @returns {Object} Bridge arc info: {startPoint, arcPoint, endPoint}
 */
export function calculateBridgeArc(crossing, waypoints, bridgeSize = 8) {
  const segmentIndex = crossing.link1 === crossing.link1 ? crossing.segment1Index : crossing.segment2Index;
  const p1 = waypoints[segmentIndex];
  const p2 = waypoints[segmentIndex + 1];

  // Calculate direction vector
  const dx = p2.x - p1.x;
  const dy = p2.y - p1.y;
  const length = Math.sqrt(dx * dx + dy * dy);

  if (length === 0) {return null;}

  // Normalize direction
  const ux = dx / length;
  const uy = dy / length;

  // Perpendicular vector (for arc height)
  const perpX = -uy;
  const perpY = ux;

  // Calculate distance from segment start to crossing point
  const crossDx = crossing.point.x - p1.x;
  const crossDy = crossing.point.y - p1.y;
  const distToCross = Math.sqrt(crossDx * crossDx + crossDy * crossDy);

  // Bridge start/end points (half bridge size before/after crossing)
  const halfBridge = bridgeSize / 2;

  const startPoint = {
    x: p1.x + ux * (distToCross - halfBridge),
    y: p1.y + uy * (distToCross - halfBridge),
  };

  const endPoint = {
    x: p1.x + ux * (distToCross + halfBridge),
    y: p1.y + uy * (distToCross + halfBridge),
  };

  // Arc control point (raised perpendicular to segment)
  const arcHeight = bridgeSize * 0.5;
  const arcPoint = {
    x: crossing.point.x + perpX * arcHeight,
    y: crossing.point.y + perpY * arcHeight,
  };

  return {
    startPoint,
    arcPoint,
    endPoint,
    crossingPoint: crossing.point,
  };
}

export default {
  detectCrossings,
  assignZOrder,
  getCrossingsForLink,
  shouldShowBridge,
  calculateBridgeArc,
};
